import { calculateMetricBaseline, indexByDate } from '@vitalai/baseline-engine';
import type { DetectedTrend, NormalizedDailyMetrics } from '@vitalai/health-models';
import { addDays, lastNDateKeys, round } from '@vitalai/shared';

const CONSECUTIVE_DAYS_THRESHOLD = 3;
const LOOKBACK_DAYS = 10;
const SLEEP_TARGET_MINUTES = 450;

function average(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function bedtimeMinutesOfDay(iso: string): number {
  const date = new Date(iso);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const totalMinutes = hours * 60 + minutes;
  // Los horarios de acostarse tipicos caen entre la noche y la madrugada; normalizamos
  // la madrugada (ej. 00:30) para que siga la escala despues de medianoche (24:30).
  return hours < 12 ? totalMinutes + 24 * 60 : totalMinutes;
}

/** Cuenta cuantos dias consecutivos, terminando en endDateKey, una metrica se mantuvo en la misma direccion respecto a su baseline. */
function countConsecutiveTrend(
  history: NormalizedDailyMetrics[],
  endDateKey: string,
  metric: 'hrv' | 'restingHeartRate',
  direction: 'up' | 'down',
): number {
  const byDate = indexByDate(history);
  let count = 0;
  let cursor = endDateKey;
  for (let i = 0; i < LOOKBACK_DAYS; i += 1) {
    const baseline = calculateMetricBaseline(history, cursor, metric, 30, byDate);
    if (!baseline || baseline.trend !== direction) break;
    count += 1;
    cursor = addDays(cursor, -1);
  }
  return count;
}

function detectSleepDebt(history: NormalizedDailyMetrics[], targetDateKey: string): DetectedTrend | null {
  const byDate = indexByDate(history);
  let deficitMinutes = 0;
  let daysBelow = 0;
  for (const key of lastNDateKeys(targetDateKey, 7)) {
    const day = byDate.get(key);
    if (!day) continue;
    const deficit = SLEEP_TARGET_MINUTES - day.sleep.totalMinutes;
    if (deficit > 0) {
      deficitMinutes += deficit;
      daysBelow += 1;
    }
  }
  if (daysBelow >= 3 && deficitMinutes >= 180) {
    const hours = round(deficitMinutes / 60, 1);
    return {
      id: `sleep_debt_${targetDateKey}`,
      type: 'sleep_debt',
      metric: 'sleepDuration',
      severity: deficitMinutes >= 300 ? 'warning' : 'info',
      daysActive: daysBelow,
      message: `Acumulaste una deuda de sueno de aproximadamente ${hours} horas en los ultimos 7 dias.`,
    };
  }
  return null;
}

function detectLaterBedtime(history: NormalizedDailyMetrics[], targetDateKey: string): DetectedTrend | null {
  const byDate = indexByDate(history);
  const recentValues = lastNDateKeys(targetDateKey, 3)
    .map((key) => byDate.get(key))
    .filter((day): day is NormalizedDailyMetrics => day !== undefined)
    .map((day) => bedtimeMinutesOfDay(day.sleep.bedtime));
  const baselineValues = lastNDateKeys(addDays(targetDateKey, -3), 30)
    .map((key) => byDate.get(key))
    .filter((day): day is NormalizedDailyMetrics => day !== undefined)
    .map((day) => bedtimeMinutesOfDay(day.sleep.bedtime));

  if (recentValues.length < 3 || baselineValues.length < 10) return null;

  const diffMinutes = average(recentValues) - average(baselineValues);
  if (diffMinutes >= 45) {
    return {
      id: `later_bedtime_${targetDateKey}`,
      type: 'later_bedtime',
      metric: 'sleepDuration',
      severity: 'info',
      daysActive: 3,
      message: `En los ultimos 3 dias te acostaste en promedio ${round(diffMinutes)} minutos mas tarde que tu habito de los ultimos 30 dias.`,
    };
  }
  return null;
}

function detectLowerRecovery(
  hrvDecliningDays: number,
  rhrRisingDays: number,
  targetDateKey: string,
): DetectedTrend | null {
  if (hrvDecliningDays >= CONSECUTIVE_DAYS_THRESHOLD && rhrRisingDays >= CONSECUTIVE_DAYS_THRESHOLD) {
    const days = Math.min(hrvDecliningDays, rhrRisingDays);
    return {
      id: `lower_recovery_${targetDateKey}`,
      type: 'lower_recovery',
      metric: 'hrv',
      severity: 'warning',
      daysActive: days,
      message: `Tu HRV y tu frecuencia cardiaca en reposo muestran senales de menor recuperacion durante los ultimos ${days} dias.`,
    };
  }
  return null;
}

function detectOvertrainingSignal(
  history: NormalizedDailyMetrics[],
  targetDateKey: string,
  hrvDecliningDays: number,
): DetectedTrend | null {
  const byDate = indexByDate(history);
  const last7Days = lastNDateKeys(targetDateKey, 7)
    .map((key) => byDate.get(key))
    .filter((day): day is NormalizedDailyMetrics => day !== undefined);
  const weeklyMinutes = last7Days.reduce((sum, day) => sum + day.activity.exerciseMinutes, 0);

  if (weeklyMinutes > 420 && hrvDecliningDays >= 2) {
    return {
      id: `overtraining_signal_${targetDateKey}`,
      type: 'overtraining_signal',
      metric: 'exerciseMinutes',
      severity: 'warning',
      daysActive: hrvDecliningDays,
      message: `Acumulaste ${weeklyMinutes} minutos de ejercicio esta semana junto con una caida en tu HRV: podria ser una senal de sobreentrenamiento.`,
    };
  }
  return null;
}

function detectNutritionDeficit(history: NormalizedDailyMetrics[], targetDateKey: string): DetectedTrend | null {
  const byDate = indexByDate(history);
  const last5Days = lastNDateKeys(targetDateKey, 5)
    .map((key) => byDate.get(key))
    .filter((day): day is NormalizedDailyMetrics => day !== undefined);
  if (last5Days.length < 5) return null;

  const lowProteinDays = last5Days.filter((day) => day.nutrition.protein < 90).length;
  if (lowProteinDays >= 4) {
    return {
      id: `nutrition_deficit_${targetDateKey}`,
      type: 'nutrition_deficit',
      metric: 'proteinIntake',
      severity: 'info',
      daysActive: lowProteinDays,
      message: `Tu consumo de proteina estuvo por debajo de 90g en ${lowProteinDays} de los ultimos 5 dias.`,
    };
  }
  return null;
}

/** Ejecuta todos los detectores de tendencias sobre el historico hasta targetDateKey. */
export function detectTrends(history: NormalizedDailyMetrics[], targetDateKey: string): DetectedTrend[] {
  const trends: DetectedTrend[] = [];

  const hrvDecliningDays = countConsecutiveTrend(history, targetDateKey, 'hrv', 'down');
  if (hrvDecliningDays >= CONSECUTIVE_DAYS_THRESHOLD) {
    trends.push({
      id: `hrv_declining_${targetDateKey}`,
      type: 'hrv_declining',
      metric: 'hrv',
      severity: hrvDecliningDays >= 5 ? 'warning' : 'info',
      daysActive: hrvDecliningDays,
      message: `Tu HRV lleva ${hrvDecliningDays} dias consecutivos por debajo de tu promedio de 30 dias.`,
    });
  }

  const rhrRisingDays = countConsecutiveTrend(history, targetDateKey, 'restingHeartRate', 'up');
  if (rhrRisingDays >= CONSECUTIVE_DAYS_THRESHOLD) {
    trends.push({
      id: `resting_hr_rising_${targetDateKey}`,
      type: 'resting_hr_rising',
      metric: 'restingHeartRate',
      severity: rhrRisingDays >= 5 ? 'warning' : 'info',
      daysActive: rhrRisingDays,
      message: `Tu frecuencia cardiaca en reposo lleva ${rhrRisingDays} dias consecutivos por encima de tu promedio de 30 dias.`,
    });
  }

  const sleepDebt = detectSleepDebt(history, targetDateKey);
  if (sleepDebt) trends.push(sleepDebt);

  const laterBedtime = detectLaterBedtime(history, targetDateKey);
  if (laterBedtime) trends.push(laterBedtime);

  const lowerRecovery = detectLowerRecovery(hrvDecliningDays, rhrRisingDays, targetDateKey);
  if (lowerRecovery) trends.push(lowerRecovery);

  const overtraining = detectOvertrainingSignal(history, targetDateKey, hrvDecliningDays);
  if (overtraining) trends.push(overtraining);

  const nutritionDeficit = detectNutritionDeficit(history, targetDateKey);
  if (nutritionDeficit) trends.push(nutritionDeficit);

  return trends;
}
